import {
  CollectionSummary,
  hasCollectionCapability,
  MAX_COLLECTION_MOVE_ITEMS,
} from '@qkindex/contracts';
import { Item } from '../../domain/item';
import { Location } from '../../domain/location';
import { recordKey } from '../collection/multiCollectionIdentity';

export type LocationRequest =
  | {
      kind: 'update';
      operationId: string;
      collectionId: string;
      items: Array<{
        id: string;
        updates: { locationId: string; location: string };
      }>;
    }
  | {
      kind: 'move';
      operationId: string;
      sourceCollectionId: string;
      destinationCollectionId: string;
      destinationLocationId: string;
      itemIds: string[];
      destinationTagIds?: string[];
    };

export interface BulkLocationResult {
  completed: string[];
  pending: string[];
  failed: string[];
  unsubmitted: string[];
}

/** Retain earlier successes while replacing the outcome of the current attempt. */
export function settleLocationResult(
  previous: BulkLocationResult,
  outcome: Pick<BulkLocationResult, 'completed' | 'pending' | 'failed'>,
): BulkLocationResult & { state: 'processing' | 'failed' | 'complete' } {
  return {
    completed: [...new Set([...previous.completed, ...outcome.completed])],
    pending: [...outcome.pending],
    failed: [...outcome.failed],
    unsubmitted: [],
    state: outcome.pending.length
      ? 'processing'
      : outcome.failed.length
        ? 'failed'
        : 'complete',
  };
}

/** Definite failures get a fresh attempt, preserving their original payload exactly. */
export function retryLocationRequest(
  request: LocationRequest,
  failed: string[],
  operationId: string,
): LocationRequest {
  const keys = new Set(failed);
  return request.kind === 'move'
    ? {
        ...request,
        operationId,
        itemIds: request.itemIds.filter((id) =>
          keys.has(recordKey({ collectionId: request.sourceCollectionId, id })),
        ),
      }
    : {
        ...request,
        operationId,
        items: request.items.filter((item) =>
          keys.has(
            recordKey({ collectionId: request.collectionId, id: item.id }),
          ),
        ),
      };
}

export interface LocationGroup {
  sourceCollectionId: string;
  items: Item[];
  destination: Location;
  unchanged: string[];
  request?:
    | Omit<Extract<LocationRequest, { kind: 'update' }>, 'operationId'>
    | Omit<Extract<LocationRequest, { kind: 'move' }>, 'operationId'>;
}

export function locationMoveError(
  items: Item[],
  destination: Location | undefined,
  collections: CollectionSummary[],
  restricted: Set<string>,
): string | undefined {
  if (!items.length) return 'Select at least one item.';
  if (items.length > MAX_COLLECTION_MOVE_ITEMS)
    return `Select at most ${MAX_COLLECTION_MOVE_ITEMS} items.`;
  if (!destination) return 'Choose a destination location.';
  const target = collections.find(
    (entry) => entry.collectionId === destination.collectionId,
  );
  if (
    !target ||
    target.lifecycle !== 'active' ||
    restricted.has(target.collectionId) ||
    !hasCollectionCapability(target.role, 'editInventory')
  ) {
    return 'The destination must be active and allow editing, with no billing restriction.';
  }
  if (
    items.some((item) => {
      const source = collections.find(
        (entry) => entry.collectionId === item.collectionId,
      );
      return (
        !source ||
        source.lifecycle !== 'active' ||
        restricted.has(source.collectionId) ||
        item.deleted ||
        !hasCollectionCapability(
          source.role,
          source.collectionId === target.collectionId
            ? 'editInventory'
            : 'moveItemsOut',
        )
      );
    })
  )
    return 'Every selected item must allow this move. Moving out requires owner or administrator access; updating within the destination requires edit access. Collections must be active and unrestricted.';
}

/** Plan only new intents. Retries must replay the original request unchanged. */
export function planLocationGroups(
  items: Item[],
  destination: Location,
): LocationGroup[] {
  const groups = new Map<string, Item[]>();
  items.forEach((item) =>
    groups.set(item.collectionId!, [
      ...(groups.get(item.collectionId!) || []),
      { ...item },
    ]),
  );
  return Array.from(groups, ([sourceCollectionId, entries]) => {
    const local = sourceCollectionId === destination.collectionId;
    // Loaded inventory may predate a completed write. The server determines no-ops.
    const changed = entries;
    return {
      sourceCollectionId,
      items: entries,
      destination: { ...destination },
      unchanged: [],
      request: !changed.length
        ? undefined
        : local
          ? {
              kind: 'update' as const,
              collectionId: sourceCollectionId,
              items: changed.map((item) => ({
                id: item.id,
                updates: {
                  locationId: destination.id,
                  location: destination.name,
                },
              })),
            }
          : {
              kind: 'move' as const,
              sourceCollectionId,
              destinationCollectionId: destination.collectionId!,
              destinationLocationId: destination.id,
              itemIds: changed.map((item) => item.id),
            },
    };
  });
}
